import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { Location } from '@app/shared/types/models/location/location.model';
import { TranslatePipe } from '@ngx-translate/core';
import { debounceTime, fromEvent } from 'rxjs';

export type TaskLocationSelectMode = 'row' | 'column';

@Component({
  selector: 'proffeo-location-select',
  templateUrl: './location-select.component.html',
  imports: [TranslatePipe, MatTooltip, MatIconModule],
  host: {
    class: 'block w-full min-w-0'
  }
})
export class LocationSelectComponent implements AfterViewInit {
  private static readonly SINGLE_LINE_HEIGHT_THRESHOLD: number = 30;

  @ViewChild('namePath')
  private readonly namePath: ElementRef;

  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  private readonly visibleLocations = signal<Location[]>([]);

  protected readonly visibleLocationsNamePath = computed(
    () =>
      this.visibleLocations()
        .map(loc => loc.name)
        .join(', ') ||
      this.location()?.name ||
      ''
  );

  protected readonly fullLocationsNamePath = computed(() => {
    return (
      this.location()
        ?.locationsPath?.map(loc => loc.name)
        .join(', ') || ''
    );
  });

  protected readonly locationPresent = computed(() => {
    const location = this.location();
    const visibleLocationsNamePath = this.visibleLocationsNamePath();
    return location !== undefined && visibleLocationsNamePath !== undefined;
  });

  protected readonly twoLines = computed(() => this.maxLines() === 2);

  public readonly location = input.required<Location>();
  public readonly minimalMode = input<boolean>(false);
  public readonly isReadonly = input<boolean>(false);
  public readonly showPlaceholder = input<boolean>(false);
  public readonly showIcon = input<boolean>(true);
  public readonly small = input<boolean>(true);
  /** Lines the path may wrap to before it is cut with an ellipsis. */
  public readonly maxLines = input<1 | 2>(1);
  public readonly locationClicked = output<void>();

  public constructor() {
    effect(() => {
      const location = this.location();
      this.syncVisibleLocationsFromLocation(location);

      untracked(() => {
        if (this.namePath?.nativeElement) {
          this.resizeNamePath();
        }
      });
    });
  }

  public ngAfterViewInit(): void {
    this.resizeNamePath();
    fromEvent(window, 'resize')
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(10))
      .subscribe(() => {
        this.resizeNamePath();
      });
  }

  private syncVisibleLocationsFromLocation(location: Location | undefined): void {
    if (!location?.locationsPath || location.locationsPath.length === 0) {
      this.visibleLocations.set(location ? [location] : []);
      return;
    }

    this.visibleLocations.set([...location.locationsPath]);
  }

  private resizeNamePath(): void {
    // Two-line mode shows the full path and lets CSS line clamping cut it.
    if (!this.namePath?.nativeElement || this.twoLines()) return;

    const namePathHeight: number = this.namePath.nativeElement.offsetHeight;
    if (namePathHeight > LocationSelectComponent.SINGLE_LINE_HEIGHT_THRESHOLD) {
      this.shrinkNamePath(1);
    } else {
      this.stretchNamePath();
    }
  }

  private stretchNamePath(): void {
    const location = this.location();
    if (!location?.locationsPath) return;

    if (this.areVisibleLocationsShorterThanAllLocations() && !this.isNamePathOver1LineHeight()) {
      this.addNextVisibleLocation();
      if (this.isNamePathOver1LineHeight()) {
        this.shrinkNamePath(location.locationsPath.length - this.visibleLocations().length);
      } else {
        this.stretchNamePath();
      }
    }
  }

  private addNextVisibleLocation(): void {
    const location = this.location();
    const visible = this.visibleLocations();

    if (!location?.locationsPath || visible.length === 0) return;

    this.visibleLocations.update(currentVisible => {
      const nextVisible = [...currentVisible];

      if (nextVisible[nextVisible.length - 1].name === '...') {
        nextVisible[nextVisible.length - 1] = location.locationsPath[nextVisible.length - 1];
      } else {
        nextVisible.push(location.locationsPath[currentVisible.length]);
      }

      return nextVisible;
    });
  }

  private areVisibleLocationsShorterThanAllLocations(): boolean {
    const visible = this.visibleLocations();
    const locationsPath = this.location()?.locationsPath;

    if (visible.length === 0 || !locationsPath) return false;

    return visible.length < locationsPath.length;
  }

  private isNamePathOver1LineHeight(): boolean {
    if (!this.namePath?.nativeElement) return false;
    return this.namePath.nativeElement.offsetHeight > LocationSelectComponent.SINGLE_LINE_HEIGHT_THRESHOLD;
  }

  private shrinkNamePath(level: number): void {
    const location = this.location();
    if (!location?.locationsPath) {
      this.visibleLocations.set([]);
      return;
    }

    const locations: Location[] = location.locationsPath;
    const nextVisible: Location[] = [];

    for (let index = 0; index < locations.length - level; index++) {
      nextVisible.push(locations[index]);
    }

    if (nextVisible.length > 0) {
      nextVisible.push({
        name: '...'
      } as Location);
    }

    this.visibleLocations.set(nextVisible);

    if (
      nextVisible.length > 1 &&
      this.namePath?.nativeElement &&
      this.namePath.nativeElement.offsetHeight > LocationSelectComponent.SINGLE_LINE_HEIGHT_THRESHOLD
    ) {
      this.shrinkNamePath(level + 1);
    }
  }

  protected onLocationClicked(): void {
    if (this.isReadonly()) return;
    this.locationClicked.emit();
  }
}
